import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useMasterData } from '../../hooks/useMasterData';
import {
  TruckIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useTextInput } from '../../hooks/useTextInput';
import { useModal } from '../../hooks/useModal';
import logger from '../../utils/logger';

interface VehicleTypeSelectorProps {
  value: string;
  onChange: (vehicleTypeId: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  error?: string;
}

interface VehicleTypeOption {
  id: string;
  name: string;
}

export const VehicleTypeSelector: React.FC<VehicleTypeSelectorProps> = ({
  value,
  onChange,
  placeholder = '車種タイプを選択してください',
  label,
  required = false,
  disabled = false,
  className = '',
  error,
}) => {
  const { vehicleTypes, addVehicleType } = useMasterData();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCustomInput, setIsCustomInput] = useState(false);
  const [customTypeName, setCustomTypeName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 統一入力処理フック（検索クエリ用）
  const searchInput = useTextInput({
    initialValue: searchQuery,
    onValueChange: value => {
      setSearchQuery(value);
    },
  });

  // 統一モーダル管理フック（ドロップダウン用）
  useModal({
    isOpen: isOpen,
    onClose: () => {
      setIsOpen(false);
      setSearchQuery('');
      searchInput.setValue('');
      setIsCustomInput(false);
      setCustomTypeName('');
    },
    containerRef: dropdownRef,
    closeOnOverlayClick: true,
    closeOnEscape: true,
  });

  // 車種タイプオプション
  const vehicleTypeOptions = useMemo((): VehicleTypeOption[] => {
    const options: VehicleTypeOption[] = [];

    // 既存の車種タイプを追加
    vehicleTypes.forEach(vehicleType => {
      options.push({
        id: vehicleType.id,
        name: vehicleType.name,
      });
    });

    // 名前順にソート
    return options.sort((a, b) => a.name.localeCompare(b.name, 'ja'));
  }, [vehicleTypes]);

  // 検索フィルタリング
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return vehicleTypeOptions;

    const query = searchQuery.toLowerCase();
    return vehicleTypeOptions.filter(option => option.name.toLowerCase().includes(query));
  }, [vehicleTypeOptions, searchQuery]);

  // 現在選択されているオプションを取得
  const selectedOption = useMemo(() => {
    return vehicleTypeOptions.find(option => option.id === value);
  }, [value, vehicleTypeOptions]);

  // 新しい車種タイプを作成
  const handleCreateCustomType = useCallback(async () => {
    if (!customTypeName.trim()) return;

    setIsCreating(true);
    try {
      const newVehicleType = await addVehicleType({ name: customTypeName.trim() });
      onChange(newVehicleType.id);
      setIsOpen(false);
      setSearchQuery('');
      searchInput.setValue('');
      setIsCustomInput(false);
      setCustomTypeName('');
    } catch (error) {
      logger.error('車種タイプの作成に失敗しました:', error);
      alert('車種タイプの作成に失敗しました。再度お試しください。');
    } finally {
      setIsCreating(false);
    }
  }, [customTypeName, addVehicleType, onChange, searchInput]);

  const handleOptionSelect = useCallback(
    (option: VehicleTypeOption) => {
      onChange(option.id);
      setIsOpen(false);
      setSearchQuery('');
      searchInput.setValue(''); // 統一入力フックもリセット
    },
    [onChange, searchInput]
  );

  const handleClear = () => {
    onChange('');
    setIsOpen(false);
    setSearchQuery('');
    searchInput.setValue(''); // 統一入力フックもリセット
    setIsCustomInput(false);
    setCustomTypeName('');
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
          {label}
          {required && <span className='text-red-500 ml-1'>*</span>}
        </label>
      )}

      <div className='relative' ref={dropdownRef}>
        {/* セレクタートリガー */}
        <div className='relative'>
          <button
            type='button'
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className={`
                          w-full px-4 py-3 text-left bg-gray-50 dark:bg-gray-700 border rounded-lg shadow-sm 
                          focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-primary-500
                          min-h-[44px] touch-target-input text-gray-900 dark:text-white
                          ${!error ? 'border-gray-300 dark:border-gray-600' : 'border-red-300 dark:border-red-600'}
                          ${disabled ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'hover:border-gray-400 dark:hover:border-gray-500'}
                          transition-colors duration-200
                      `}
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center'>
                <TruckIcon className='h-5 w-5 text-gray-400 mr-3' />
                <span
                  className={
                    selectedOption
                      ? 'text-gray-900 dark:text-white'
                      : 'text-gray-500 dark:text-gray-400'
                  }
                >
                  {selectedOption ? selectedOption.name : placeholder}
                </span>
              </div>
              <ChevronDownIcon
                className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </button>
        </div>

        {/* ドロップダウンメニュー */}
        {isOpen && !disabled && (
          <div className='absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-80 overflow-hidden'>
            {/* 検索ボックス */}
            <div className='p-3 border-b border-gray-200 dark:border-gray-700'>
              <div className='relative'>
                <MagnifyingGlassIcon className='h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2' />
                <input
                  type='text'
                  {...searchInput.inputProps}
                  placeholder='車種タイプを検索...'
                  className='w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                />
              </div>
            </div>

            {/* オプションリスト */}
            <div className='max-h-64 overflow-y-auto'>
              {/* クリアオプション */}
              {selectedOption && (
                <button
                  type='button'
                  onClick={handleClear}
                  className='w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'
                >
                  選択をクリア
                </button>
              )}

              {/* 新しい車種タイプを作成オプション */}
              {!isCustomInput && (
                <button
                  type='button'
                  onClick={() => setIsCustomInput(true)}
                  className='w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 text-green-600 dark:text-green-400 flex items-center'
                >
                  <PlusIcon className='h-4 w-4 mr-3' />
                  <span className='font-medium'>新しい車種タイプを作成</span>
                </button>
              )}

              {/* カスタム入力フォーム */}
              {isCustomInput && (
                <div className='p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700'>
                  <div className='space-y-3'>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                        新しい車種タイプ名
                      </label>
                      <input
                        type='text'
                        value={customTypeName}
                        onChange={e => setCustomTypeName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCreateCustomType();
                          } else if (e.key === 'Escape') {
                            setIsCustomInput(false);
                            setCustomTypeName('');
                          }
                        }}
                        placeholder='例: 特殊車両、重機運搬車'
                        className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                        autoFocus
                      />
                    </div>
                    <div className='flex space-x-2'>
                      <button
                        type='button'
                        onClick={handleCreateCustomType}
                        disabled={!customTypeName.trim() || isCreating}
                        className='flex-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium'
                      >
                        {isCreating ? '作成中...' : '作成'}
                      </button>
                      <button
                        type='button'
                        onClick={() => {
                          setIsCustomInput(false);
                          setCustomTypeName('');
                        }}
                        className='px-3 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm font-medium'
                      >
                        キャンセル
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* 車種タイプオプション */}
              {!isCustomInput &&
                (filteredOptions.length > 0 ? (
                  filteredOptions.map(option => (
                    <button
                      key={option.id}
                      type='button'
                      onClick={() => handleOptionSelect(option)}
                      className={`
                                          w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
                                          ${
                                            selectedOption?.id === option.id
                                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                                              : 'text-gray-900 dark:text-white'
                                          }
                                      `}
                    >
                      <div className='flex items-center'>
                        <TruckIcon className='h-4 w-4 text-gray-400 mr-3' />
                        <div>
                          <div className='font-medium'>{option.name}</div>
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className='px-4 py-3 text-gray-500 dark:text-gray-400 text-center'>
                    {searchQuery ? '検索結果が見つかりません' : '利用可能な車種タイプがありません'}
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {error && <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{error}</p>}
    </div>
  );
};

export default VehicleTypeSelector;
